import Spinner from "presentation/components/Shared/Spinner";
import { Column } from "primereact/column";
import {
  DataTable,
  DataTableFilterEvent,
  DataTableFilterMetaData,
  DataTablePageEvent,
  DataTableSortEvent,
} from "primereact/datatable";
import { Toast } from "primereact/toast";
import { useEffect, useRef, useState } from "react";
import { confirmDialog } from "primereact/confirmdialog";
import { useParameterStore } from "presentation/stores/use-parameter-store";
import { useSupportTripByFilter } from "presentation/hooks/use-query-support-trip";
import { SupportTripInputFilter } from "domain/models/support-trip-input-filter";
import { useSupportTripCancelStore } from "presentation/stores/use-support-trip-cancel-store";
import { useAuthStore } from "presentation/stores/use-auth-store";
import { formatDateToPT } from "presentation/utils/formatDateToPT";
import { useFilterSupportTripStore } from "presentation/stores/use-filter-support-trip-store";
import { useFilterAdminSupportTripStore } from "presentation/stores/use-filter-admin-support-trip-store";
import { SupportTrip } from "domain/entities/support-trip";
import ApprovalForm from "./ApprovalForm";
import { Dialog } from "primereact/dialog";
import { MessageTypeEnum } from "domain/enum/messageType";

interface SupportTripsGridProps {
  isAdmin: boolean
}

const SupportTripsGrid: React.FC<SupportTripsGridProps> = ({ isAdmin }) => {

  const possibleApprovalStatus = ["A Aprovar"];
  const possibleCancelStatus = ["Aprovado", "A Aprovar"];

  const { userEmployee: userEmployee } = useAuthStore();

  const { filters: filterAdminSupportTrip } = useFilterAdminSupportTripStore();

  const { filters, setFilters } = useFilterSupportTripStore();
  const toast = useRef<Toast>(null);
  const [lazyParams, setLazyParams] = useState({
    sortBy: "dateTrip",
    sortOrder: "asc",
    limit: 10,
    page: 1,
    filters: {},
  });

  const {
    data: supportTripsData,
    isLoading,
    isError,
    error,
    refetch,
    isPending,
    isFetching
  } = useSupportTripByFilter({
    month: filterAdminSupportTrip?.month ?? undefined,
    year: filterAdminSupportTrip?.year ?? undefined,
    status: filterAdminSupportTrip?.status ?? undefined,
    employeeId: isAdmin ? filterAdminSupportTrip?.employeeId ?? undefined : userEmployee?.id,
    sortBy: lazyParams.sortBy,
    sortOrder: lazyParams.sortOrder,
    page: lazyParams.page,
    limit: lazyParams.limit,
    ...lazyParams.filters,
  } as SupportTripInputFilter);

  const confirmAction = (message: string, acceptCallback: () => void) => {
    confirmDialog({
      message: message,
      header: "Confirmação",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Sim",
      rejectLabel: "Não",
      accept: acceptCallback,
    });
  };

  const { setSupportTripToCancel } = useSupportTripCancelStore();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [supportTripToApprove, setSupportTripToApprove] = useState<SupportTrip>();


  useEffect(() => {
    refetch();
  }, [lazyParams]);

  useEffect(() => {
    setLazyParams((prev) => ({
      ...prev,
      filters: Object.fromEntries(
        Object.entries(filters)
          .map(([key, value]) => {
            if ("constraints" in value) {
              return [key, value.constraints?.[0]?.value ?? ""];
            }
            return [key, (value as DataTableFilterMetaData)?.value ?? ""];
          })
          .filter(
            ([_, value]) =>
              value !== null && value !== undefined && value !== ""
          )
      ),
    }));
  }, [filters]);

  useEffect(() => {
    refetch();
  }, [lazyParams.filters]);

  useEffect(() => {
    refetch();
  }, [filterAdminSupportTrip.month, filterAdminSupportTrip.year, filterAdminSupportTrip.status, filterAdminSupportTrip.employeeId]);

  if (isError && toast.current) {
    toast.current.show({
      severity: "error",
      summary: "Erro",
      detail: error?.message || "Ocorreu um erro inesperado",
      life: 3000,
    });
  }

  const handleCancel = (rowData: SupportTrip) => {
    setSupportTripToCancel(rowData);
  };

  const showActionOptions = (rowData: SupportTrip) => {
    return (
      <div className="spacing-icons">
        {!isAdmin &&
          rowData.employee.id === userEmployee?.id &&
          possibleCancelStatus.includes(rowData.status) &&
          (<i
            className="pi pi-trash cursor-pointer"
            onClick={() =>
              confirmAction("Deseja cancelar esta viagem?", () =>
                handleCancel(rowData)
              )
            }
          />)
        }

        {"  "}
        {isAdmin &&
          possibleApprovalStatus.includes(rowData.status) &&
          (
            <i
              className="pi pi-file-check cursor-pointer"
              onClick={() => {
                setSupportTripToApprove(rowData);
                setIsModalVisible(true);
              }}
            />
          )
        }

      </div>
    );
  };

  const handleCloseModal = async () => {
    setIsModalVisible(false);
    setSupportTripToApprove(undefined);
    refetch();
  };

  const showMessage = (
    type: MessageTypeEnum,
    subject: string,
    message: string
  ) => {
    toast.current?.show({
      severity: type,
      summary: subject,
      detail: message,
      life: 2500,
    });
  };

  if (isPending || isLoading || isFetching) {
    return <Spinner />;
  }

  return (
    <div>
      <DataTable
        value={supportTripsData?.data || []}
        lazy
        paginator
        first={(lazyParams.page - 1) * lazyParams.limit}
        rows={lazyParams.limit}
        totalRecords={supportTripsData?.pagination?.count || 0}
        loading={isLoading}
        onPage={(e: DataTablePageEvent) =>
          setLazyParams({
            ...lazyParams,
            page: e.page ? e.page + 1 : 1,
            limit: e.rows,
          })
        }
        sortField={lazyParams.sortBy}
        sortOrder={lazyParams.sortOrder === "asc" ? 1 : -1}
        onSort={(e: DataTableSortEvent) =>
          setLazyParams({
            ...lazyParams,
            sortBy: e.sortField,
            sortOrder:
              e.sortOrder === -1 ? "desc" : e.sortOrder === 1 ? "asc" : "",
          })
        }
        onFilter={(e: DataTableFilterEvent) => setFilters(e.filters)}
        filters={filters}
        size="small"
        resizableColumns
        columnResizeMode="expand"
        rowsPerPageOptions={[5, 10, 25, 50]}
        paginatorLeft
        dataKey="id"
        globalFilterFields={[
          "locationOrigin",
          "locationDestination",
          "employee.name",
          "status"
        ]}
      >
        <Column
          field="employee.name"
          header="Colaborador"
          sortable
          showFilterMatchModes={false}
        />
        <Column
          field="dateTrip"
          header="Data"
          sortable
          showFilterMatchModes={false}
          body={(rowData) => formatDateToPT(rowData.dateTrip)}
        />
        <Column
          field="locationOrigin"
          header="Local de origem"
          filter
          sortable
          showFilterMatchModes={false}
        />
        <Column
          field="locationDestination"
          header="Local de destino"
          filter
          sortable
          showFilterMatchModes={false}
        />
        <Column
          field="distanceKm"
          header="Distância"
          sortable
          showFilterMatchModes={false}
          body={(rowData) => `${rowData.distanceKm} km`}
        />
        <Column
          field="tripValue"
          header="Valor"
          sortable
          showFilterMatchModes={false}
          body={(rowData) => `${rowData.tripValue.toFixed(2)} €`}
        />
        <Column
          field="status"
          header="Estado"
          filter={isAdmin}
          sortable
          showFilterMatchModes={false}
          body={(rowData) => {
            if (rowData.status === "Não Aprovado") {
              return (
                <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                  <span>{rowData.status}</span>
                  <i
                    className="pi pi-info-circle"
                    style={{ cursor: "pointer" }}
                    title={`Motivo da rejeição: ${rowData.rejectionReason}`}
                  />
                </div>
              );
            }
            return <span>{rowData.status}</span>;
          }}
        />
        <Column
          body={showActionOptions}
          header="Ações"
          align="center"
          alignHeader="center"
        />
      </DataTable>

      <Dialog
        header={`Detalhes da viagem a Aprovar`}
        visible={isModalVisible}
        style={{ width: "20vw", height: "40vh" }}
        onHide={handleCloseModal}
      >
        <ApprovalForm
          supportTrip={supportTripToApprove}
          onClose={handleCloseModal}
          showMessage={showMessage}
        />
      </Dialog>

      <Toast ref={toast} position="bottom-right" />
    </div>
  );
};

export default SupportTripsGrid;
